import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminLogin, adminLogout, adminVerifyToken } from '../utils/api';
import API from '../utils/api';

/**
 * Custom hook for admin authentication
 * @returns {Object} Authentication state and functions
 */
const useAdminAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminEmail, setAdminEmail] = useState(null);
  const navigate = useNavigate();

  // Check if admin is already logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('adminAuth');
      const email = localStorage.getItem('adminEmail');
      const isAdmin = localStorage.getItem('isAdmin') === 'true';
      
      if (token && isAdmin) {
        try {
          // Verify token with the backend
          API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await adminVerifyToken();
          
          if (response && response.success) {
            setIsAuthenticated(true);
            setAdminEmail(email);
          } else {
            // Token is invalid, clear local storage
            localStorage.removeItem('adminAuth');
            localStorage.removeItem('adminEmail');
            localStorage.removeItem('isAdmin');
          }
        } catch (err) {
          console.error('Token verification error:', err);
          // Clear localStorage on error
          localStorage.removeItem('adminAuth');
          localStorage.removeItem('adminEmail');
          localStorage.removeItem('isAdmin');
        }
      }
      
      setIsLoading(false);
    };
    
    checkAuth();
  }, []);

  /**
   * Login function
   * @param {Object} credentials - Admin credentials
   * @returns {Promise} Login result
   */
  const login = async (credentials) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await adminLogin(credentials);
      setIsAuthenticated(true);
      setAdminEmail(credentials.email);
      setIsLoading(false);
      return result;
    } catch (err) {
      setError(err.message || 'Login failed');
      setIsLoading(false);
      throw err;
    }
  };

  /**
   * Logout function
   */
  const logout = () => {
    adminLogout();
    setIsAuthenticated(false);
    setAdminEmail(null);
    navigate('/admin/login');
  };

  /**
   * Require authentication - redirect to login if not authenticated
   */
  const requireAuth = () => {
    if (!isLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  };

  return {
    isAuthenticated,
    isLoading,
    error,
    adminEmail,
    login,
    logout,
    requireAuth
  };
};

export default useAdminAuth; 